import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AppDispatch, RootState } from '../api/store';
import { clearBillSuccess, fetchBill } from '../api/slices/billSlice';

const Bill: React.FC = () => {
  const { ref } = useParams<{ ref: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const navigate = useNavigate();
  const { billData,billError,billLoading,billSuccess } = useSelector(
    (state: RootState) => state.bill
  );

  useEffect(() => {
    if (ref) {
      dispatch(fetchBill(ref));
    }
    return () => {
      if (billSuccess) {
        dispatch(clearBillSuccess());
      }
    };
  }, [ref, dispatch]);

  const formatLKR = (amount: number) => {
    return new Intl.NumberFormat('en-LK', {
      style: 'currency',
      currency: 'LKR',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  

  if (billLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (billError) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {typeof billError === 'string' ? billError : 'Failed to load bill data'}
        </div>
      </div>
    );
  }

  if (!billData) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          No bill data available
        </div>
      </div>
    );
  }

  return (
  <div className="px-4 sm:px-20 lg:px-56 mb-10 mt-20">
  {/* Enhanced Animated Button - Moved to Top */}
  <div className="mb-8 flex justify-center">
       <button
      onClick={() => navigate('/')} 
      className="relative inline-flex items-center justify-center px-6 py-3 overflow-hidden font-bold text-white rounded-lg shadow-2xl group transition-all duration-300 hover:shadow-green-500/30"
    >
      {/* Animated background */}
      <span className="absolute inset-0 w-full h-full transition-all duration-1000 ease-out bg-gradient-to-br from-green-500 via-green-600 to-green-700 group-hover:from-green-600 group-hover:via-green-700 group-hover:to-green-800 animate-gradient"></span>
      
      {/* Light effects - Always animated */}
      <span className="absolute top-0 left-0 w-full bg-gradient-to-b from-white to-transparent opacity-10 h-1/3 animate-light-top"></span>
      <span className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-white to-transparent opacity-10 animate-light-bottom"></span>
      <span className="absolute bottom-0 left-0 w-4 h-full bg-gradient-to-r from-white to-transparent opacity-10 animate-light-left"></span>
      <span className="absolute bottom-0 right-0 w-4 h-full bg-gradient-to-l from-white to-transparent opacity-10 animate-light-right"></span>
      
      {/* Ripple effect - Always animated */}
      <span className="absolute inset-0 w-full h-full overflow-hidden">
        <span className="absolute block w-64 h-64 -top-32 -left-32 rounded-full opacity-20 animate-ripple"></span>
        <span className="absolute block w-64 h-64 -top-32 -left-32 rounded-full opacity-15 animate-ripple-delay"></span>
      </span>
      
      {/* Border */}
      <span className="absolute inset-0 border-2 border-white rounded-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"></span>
      
      {/* Content - Updated text and icon */}
      <span className="relative flex items-center text-sm sm:text-base">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10.707 2.293a1 1 0 010 1.414L7.414 7H15a1 1 0 110 2H7.414l3.293 3.293a1 1 0 01-1.414 1.414l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 0z"
            clipRule="evenodd"
          />
        </svg>
        Return to Home
      </span>
    </button>

  </div>

  <div className="px-2">
    <div
      id="bill-container"
      className="container mx-auto p-2 sm:p-4 bg-white shadow-lg rounded-lg overflow-hidden relative min-h-[500px]"
    >
      {/* Centered Watermark - Visible on all devices */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <div className="font-bold text-3xl sm:text-5xl md:text-6xl text-gray-200 opacity-30 transform -rotate-45 select-none">
          Ceylon Hospital
        </div>
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-2 items-center bg-gray-100 p-2 relative z-10">
        <div className="w-full sm:w-1/2 flex flex-col justify-center items-center">
          <div className="bg-green-600 w-full">
            <p className="text-white text-center py-2 sm:py-4 text-xl sm:text-2xl md:text-3xl font-bold">
              Ceylon Hospital
            </p>
          </div>
          <p className="text-xs sm:text-sm font-semibold text-center mt-1">
            Sri Lanka's 1st Indigenous Medicine Private Hospital
          </p>
        </div>
        <div className="w-full sm:w-1/2 flex flex-col justify-center items-center mt-2 sm:mt-0 space-y-1">
          <p className="text-xs text-center">
            Hotline: (+94)71-250-9988
          </p>
          <p className="text-xs text-center">
            Email: info@ceylonhospital.com
          </p>
          <p className="text-xs text-center">
            No.228/1, Asiri Mawatha, Kandy Road, Yakkala.
          </p>
        </div>
      </div>
      
      <hr className="border-gray-300 relative z-10" />
      
      {/* Patient and Invoice Details */}
      <div className="mt-2.5 flex flex-col sm:flex-row px-2 sm:px-7 py-4 relative z-10">
        <div className="w-full sm:w-1/2 flex flex-col mb-4 sm:mb-0 space-y-1">
          <p className="font-bold text-sm sm:text-md">Patient Details:</p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">Name:</span> {billData.patientProfile.name}
          </p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">REG NO:</span> {billData.patientProfile.ch_patient_id}
          </p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">NIC:</span> {billData.patientProfile.nic}
          </p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">Appointment Date:</span> {billData.doctorTimeslot.doctorDate.date}
          </p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">Appointment Time:</span> {billData.doctorTimeslot.time_sloat}
          </p>
        </div>
        <div className="w-full sm:w-1/2 flex flex-col sm:items-end">
          <p className="font-bold text-sm sm:text-md">Invoice Details:</p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">Invoice Number:</span> {billData.paymentStatement.id}
          </p>
          <p className="text-xs sm:text-sm">
            <span className="font-semibold">Date:</span> {billData.paymentStatement.date}
          </p>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto mb-6 px-1 sm:px-3 relative z-10">
        <table className="w-full border border-gray-300">
          <thead className="bg-green-600 text-white">
            <tr>
              <th className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">#</th>
              <th className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">Description</th>
              <th className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">Total</th>
              <th className="px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">Amount (LKR)</th>
            </tr>
          </thead>
          <tbody>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">01</td>
              <td className="border px-2 py-1 sm:px-4 sm:py-2 italic text-xs sm:text-sm">
                Charges for appointment recommended by {billData.doctorTimeslot.doctorDate.doctor.name}
              </td>
              <td className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {formatLKR(billData.paymentStatement.doctor_charge)}
              </td>
              <td className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {formatLKR(billData.paymentStatement.doctor_charge)}
              </td>
            </tr>
            <tr className="hover:bg-gray-50 transition-colors">
              <td className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">02</td>
              <td className="border px-2 py-1 sm:px-4 sm:py-2 italic text-xs sm:text-sm">
                Hospital Charges
              </td>
              <td className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {formatLKR(billData.paymentStatement.hospital_charge)}
              </td>
              <td className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {formatLKR(billData.paymentStatement.hospital_charge)}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={3} className="border px-2 py-1 sm:px-4 sm:py-2 text-right text-xs sm:text-sm">
                Sub Total
              </th>
              <th className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {formatLKR(billData.paymentStatement.doctor_charge + billData.paymentStatement.hospital_charge)}
              </th>
            </tr>
            <tr>
              <th colSpan={3} className="border px-2 py-1 sm:px-4 sm:py-2 text-right text-xs sm:text-sm">
                Discount (%)
              </th>
              <th className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                0.00
              </th>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <th colSpan={3} className="border px-2 py-1 sm:px-4 sm:py-2 text-right text-xs sm:text-sm">
                Net Total
              </th>
              <th className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {formatLKR(billData.paymentStatement.doctor_charge + billData.paymentStatement.hospital_charge)}
              </th>
            </tr>
            <tr>
              <th colSpan={3} className="border px-2 py-1 sm:px-4 sm:py-2 text-right text-xs sm:text-sm">
                Payment Method
              </th>
              <th className="border px-2 py-1 sm:px-4 sm:py-2 text-xs sm:text-sm">
                {billData.paymentStatement.paymentMethod.name}
              </th>
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Footer */}
      <div className="text-xs sm:text-sm text-gray-600 p-1 relative z-10">
        <p className="text-center">
          <span className="font-semibold">
            Generated by: Ceylon Hospital | Issued Date: {new Date().toLocaleString('en-LK', {
              timeZone: 'Asia/Colombo',
            })}
          </span>
          <br />
          <span className="italic">
            This document is authorized to be used only for billing
            purposes and contains confidential patient information.
          </span>
          <br />
          <span className="font-semibold">
            All the payments are permanent and non-refundable.
          </span>
        </p>
      </div>
    </div>
  </div>

  {/* Enhanced Animated Button */}
  <div className="mt-8 flex justify-center">
    <button
      onClick={() => navigate('/book-appointment')}
      className="relative inline-flex items-center justify-center px-8 py-4 overflow-hidden font-bold text-white rounded-lg shadow-2xl group transition-all duration-300 hover:shadow-green-500/30"
    >
      {/* Animated background */}
      <span className="absolute inset-0 w-full h-full transition-all duration-300 ease-out bg-gradient-to-br from-green-500 to-green-700 group-hover:from-green-600 group-hover:to-green-800"></span>
      
      {/* Light effects */}
      <span className="absolute top-0 left-0 w-full bg-gradient-to-b from-white to-transparent opacity-10 h-1/3 animate-light-top"></span>
      <span className="absolute bottom-0 left-0 w-full h-1/3 bg-gradient-to-t from-white to-transparent opacity-10 animate-light-bottom"></span>
      <span className="absolute bottom-0 left-0 w-4 h-full bg-gradient-to-r from-white to-transparent opacity-10 animate-light-left"></span>
      <span className="absolute bottom-0 right-0 w-4 h-full bg-gradient-to-l from-white to-transparent opacity-10 animate-light-right"></span>
      
      {/* Ripple effect */}
      <span className="absolute inset-0 w-full h-full transition-all duration-300 ease-out opacity-0 group-hover:opacity-100">
        <span className="absolute block w-64 h-64 mx-auto mt-10 rounded-full group-hover:animate-ripple"></span>
      </span>
      
      {/* Border */}
      <span className="absolute inset-0 border-2 border-white rounded-lg opacity-20 group-hover:opacity-30 transition-opacity duration-300"></span>
      
      {/* Content */}
      <span className="relative flex items-center text-sm sm:text-base">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="w-5 h-5 mr-2 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
            clipRule="evenodd"
          />
        </svg>
        Book Your Next Appointment
      </span>
    </button>
  </div>
</div>
  );
};

export default Bill;